import React, { useState, useEffect } from 'react';
import {
  ShieldCheck, Plus, Clock, Check, X, AlertCircle,
  ChevronDown, Loader2, User, Calendar, DollarSign,
  MessageSquare, Wallet, Play, Ban, Filter
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';
import DemandesApprobationSection from './DemandesApprobationSection';

// ============================================================================
// VALIDATION MODULE — Demandes de dépenses + approbation
// ============================================================================

const STATUT_CONFIG = {
  en_attente: { label: 'En attente', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  approuvee: { label: 'Approuvée', color: '#10b981', bg: '#d4f4dd', icon: Check },
  rejetee: { label: 'Rejetée', color: '#ef4444', bg: '#fee2e2', icon: X },
  executee: { label: 'Exécutée', color: '#6366f1', bg: '#e0e7ff', icon: Play },
  annulee: { label: 'Annulée', color: '#9ca3af', bg: '#f3f4f6', icon: Ban },
};

const PRIORITE_CONFIG = {
  basse: { label: 'Basse', color: '#9ca3af', bg: '#f3f4f6' },
  normale: { label: 'Normale', color: '#3b82f6', bg: '#eff6ff' },
  haute: { label: 'Haute', color: '#f59e0b', bg: '#fef3c7' },
  urgente: { label: 'Urgente', color: '#ef4444', bg: '#fee2e2' },
};

const ValidationModule = () => {
  const [demandes, setDemandes] = useState([]);
  const [totaux, setTotaux] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [filterStatut, setFilterStatut] = useState('');

  // Modal création
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    objet: '', description: '', montant: '', categorie: '', priorite: 'normale',
    date_demande: new Date().toISOString().split('T')[0],
  });

  // Actions
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [showExecuteModal, setShowExecuteModal] = useState(null);
  const [selectedCaisse, setSelectedCaisse] = useState('');
  const [caisses, setCaisses] = useState([]);
  const [categories, setCategories] = useState([]);

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole } = useParamsCompta();
  const showToast = (m, t = 'success') => setToast({ show: true, message: m, type: t });

  useEffect(() => {
    if (toast.show) { const t = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000); return () => clearTimeout(t); }
  }, [toast.show]);

  // ============================================================================
  // CHARGEMENT
  // ============================================================================
  useEffect(() => { loadDemandes(); loadCaisses(); loadCategories(); }, []);
  useEffect(() => { loadDemandes(); }, [filterStatut]);

  const loadDemandes = async () => {
    showLoader(); setIsLoading(true);
    try {
      const r = await api.validation.getAll({ statut: filterStatut });
      if (r.success) { setDemandes(r.data); setTotaux(r.totaux || {}); }
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const loadCaisses = async () => {
    try { const r = await api.tresorerie.getCaisses(); if (r.success) setCaisses(r.data.filter(c => c.statut === 'active')); } catch (e) {}
  };

  const loadCategories = async () => {
    try { const r = await api.tresorerie.getCategories(); if (r.success) setCategories(r.data); } catch (e) {}
  };

  // ============================================================================
  // CRUD
  // ============================================================================
  const openCreateModal = () => {
    setFormData({ objet: '', description: '', montant: '', categorie: '', priorite: 'normale', date_demande: new Date().toISOString().split('T')[0] });
    setShowModal(true);
  };

  const handleCreate = async () => {
    if (!formData.objet.trim()) { showToast("L'objet est obligatoire", 'error'); return; }
    if (!formData.montant || parseFloat(formData.montant) <= 0) { showToast('Montant invalide', 'error'); return; }
    setIsSaving(true);
    try {
      const r = await api.validation.creer({ ...formData, montant: parseFloat(formData.montant) });
      if (r.success) {
        if (r.approbation_requise) {
          showToast('Demande soumise à approbation, en attente de validation', 'info');
        } else {
          showToast(r.message);
        }
        setShowModal(false);
        await loadDemandes();
      }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
    finally { setIsSaving(false); }
  };

  // ============================================================================
  // ACTIONS WORKFLOW
  // ============================================================================
  const handleApprouver = async (id) => {
    try {
      const r = await api.validation.approuver(id);
      if (r.success) { showToast(r.message); await loadDemandes(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };

  const handleRejeter = async () => {
    if (!showRejectModal) return;
    try {
      const r = await api.validation.rejeter(showRejectModal.id, { commentaire: rejectComment });
      if (r.success) { showToast(r.message); setShowRejectModal(null); setRejectComment(''); await loadDemandes(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };

  const handleExecuter = async () => {
    if (!showExecuteModal || !selectedCaisse) { showToast('Sélectionnez une caisse', 'error'); return; }
    try {
      const r = await api.validation.executer(showExecuteModal.id, { caisse_id: parseInt(selectedCaisse) });
      if (r.success) { showToast(r.message); setShowExecuteModal(null); setSelectedCaisse(''); await loadDemandes(); }
      else showToast(r.message || 'Erreur', 'error');
    } catch (e) { showToast('Erreur', 'error'); }
  };


  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #f59e0b', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Clock size={18} className="text-amber-600" /><span className="text-xs font-semibold text-gray-500">En attente</span></div>
          <div className="text-2xl font-bold text-amber-600">{totaux.en_attente || 0}</div>
          <div className="text-xs text-gray-400 mt-1">{formatMontant(totaux.montant_en_attente)}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #10b981', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><Check size={18} className="text-green-600" /><span className="text-xs font-semibold text-gray-500">Approuvées</span></div>
          <div className="text-2xl font-bold text-green-600">{totaux.approuvees || 0}</div>
          <div className="text-xs text-gray-400 mt-1">{formatMontant(totaux.montant_approuve)}</div>
        </div>
        <div className="bg-white rounded-2xl p-5" style={{ borderLeft: '3px solid #448696', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={18} style={{ color: '#448696' }} /><span className="text-xs font-semibold text-gray-500">Total demandes</span></div>
          <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{totaux.total || 0}</div>
        </div>
        <div onClick={openCreateModal} className="bg-white rounded-2xl p-5 cursor-pointer group transition-all hover:shadow-md" style={{ border: '2px dashed #d1d5db' }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = '#448696'} onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'}>
          <div className="flex items-center justify-center h-full gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#f0f9fb' }}><Plus size={22} style={{ color: '#448696' }} /></div>
            <div><div className="text-sm font-bold text-gray-700 group-hover:text-[#448696] transition-colors">Nouvelle demande</div><div className="text-xs text-gray-400">Soumettre une dépense</div></div>
          </div>
        </div>

      
      </div>
        <DemandesApprobationSection
          typeDocument="depense"
          titre="Demandes soumises à approbation"
          onRefresh={loadDemandes}
        />

      {/* Filtres */}
      <div className="flex gap-2 mb-6">
        {[{ value: '', label: 'Toutes' }, ...Object.entries(STATUT_CONFIG).map(([k, v]) => ({ value: k, label: v.label, color: v.color, bg: v.bg }))].map(f => (
          <button key={f.value} onClick={() => setFilterStatut(f.value)}
            className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all border-2 ${filterStatut === f.value ? 'shadow-sm' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            style={filterStatut === f.value ? { borderColor: f.color || '#448696', backgroundColor: f.bg || '#f0f9fb', color: f.color || '#448696' } : {}}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {isLoading ? null : demandes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <ShieldCheck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">Aucune demande</h3>
          <p className="text-sm text-gray-500">Soumettez une demande de dépense pour démarrer le workflow.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {demandes.map((d) => {
            const statut = STATUT_CONFIG[d.statut] || STATUT_CONFIG.en_attente;
            const priorite = PRIORITE_CONFIG[d.priorite] || PRIORITE_CONFIG.normale;
            const StatutIcon = statut.icon;

            return (
              <div key={d.id} className="bg-white rounded-2xl border border-gray-100 p-5 transition-all hover:shadow-md" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="flex items-start gap-4">
                  {/* Icône statut */}
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mt-0.5" style={{ backgroundColor: statut.bg }}>
                    <StatutIcon size={20} style={{ color: statut.color }} />
                  </div>

                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-gray-900">{d.objet}</span>
                      <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: statut.bg, color: statut.color }}>{statut.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded-md font-semibold" style={{ backgroundColor: priorite.bg, color: priorite.color }}>{priorite.label}</span>
                    </div>

                    {d.description && <p className="text-xs text-gray-500 mb-2 line-clamp-1">{d.description}</p>}

                    <div className="flex items-center gap-4 text-xs text-gray-400 font-medium">
                      <span className="flex items-center gap-1"><User size={11} />{d.demandeur_nom}</span>
                      <span className="flex items-center gap-1"><Calendar size={11} />{formatDate(d.date_demande)}</span>
                      {d.categorie && <span>{d.categorie}</span>}
                      {d.valideur_nom && <span className="flex items-center gap-1"><ShieldCheck size={11} />{d.valideur_nom}</span>}
                      {d.caisse_nom && <span className="flex items-center gap-1"><Wallet size={11} />{d.caisse_nom}</span>}
                    </div>

                    {d.commentaire_validation && (
                      <div className="mt-2 p-2 rounded-lg bg-gray-50 text-xs text-gray-600 flex items-start gap-2">
                        <MessageSquare size={12} className="mt-0.5 text-gray-400 shrink-0" />
                        <span>{d.commentaire_validation}</span>
                      </div>
                    )}
                  </div>

                  {/* Montant */}
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold" style={{ color: '#1a1a2e' }}>{formatMontant(d.montant)}</div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 shrink-0">
                    {d.statut === 'en_attente' && (
                      <>
                        <button onClick={() => handleApprouver(d.id)} title="Approuver"
                          className="w-9 h-9 rounded-xl flex items-center justify-center bg-green-50 hover:bg-green-100 transition-all">
                          <Check size={16} className="text-green-600" />
                        </button>
                        <button onClick={() => { setShowRejectModal(d); setRejectComment(''); }} title="Rejeter"
                          className="w-9 h-9 rounded-xl flex items-center justify-center bg-red-50 hover:bg-red-100 transition-all">
                          <X size={16} className="text-red-600" />
                        </button>
                      </>
                    )}
                    {d.statut === 'approuvee' && (
                      <button onClick={() => { setShowExecuteModal(d); setSelectedCaisse(''); }} title="Exécuter"
                        className="px-3 py-2 rounded-xl text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all flex items-center gap-1">
                        <Play size={14} /> Exécuter
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL CRÉATION */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <Plus size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Nouvelle demande</h3>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Objet *</label>
              <input type="text" value={formData.objet} onChange={(e) => setFormData({ ...formData, objet: e.target.value })} placeholder="Ex: Achat fournitures bureau..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{`Montant (${symbole}) *`}</label>
              <input type="number" value={formData.montant} onChange={(e) => setFormData({ ...formData, montant: e.target.value })} placeholder="0"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium text-lg focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie</label>
                <select value={formData.categorie} onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none">
                  <option value="">-</option>
                  {categories.filter(c => c.type_mouvement === 'debit' || c.type_mouvement === 'les_deux').map(c => (
                    <option key={c.id} value={c.nom}>{c.nom}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Priorité</label>
                <select value={formData.priorite} onChange={(e) => setFormData({ ...formData, priorite: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none">
                  {Object.entries(PRIORITE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="2"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(92,149,163,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }} />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleCreate} disabled={isSaving}
                className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg transition-all disabled:opacity-50"
                style={{ backgroundColor: '#448696' }}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Soumettre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL REJET */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#ef4444' }}>
              <X size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Rejeter la demande</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Rejeter "{showRejectModal.objet}" ({formatMontant(showRejectModal.montant)}) ?</p>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif du rejet</label>
              <textarea value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} rows="2" placeholder="Raison..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowRejectModal(null)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleRejeter} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg transition-all" style={{ backgroundColor: '#ef4444' }}>Rejeter</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EXÉCUTION */}
      {showExecuteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#6366f1' }}>
              <Play size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Exécuter la dépense</h3>
            </div>
            <p className="text-sm text-gray-600 mb-1">Dépense : <strong>{showExecuteModal.objet}</strong></p>
            <p className="text-lg font-bold mb-4" style={{ color: '#1a1a2e' }}>{formatMontant(showExecuteModal.montant)}</p>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Caisse à débiter *</label>
              <select value={selectedCaisse} onChange={(e) => setSelectedCaisse(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none">
                <option value="">Sélectionner...</option>
                {caisses.map(c => <option key={c.id} value={c.id}>{c.nom} ({formatMontant(c.solde_actuel)})</option>)}
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowExecuteModal(null)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleExecuter} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg transition-all" style={{ backgroundColor: '#6366f1' }}>Exécuter</button>
            </div>
          </div>
        </div>
      )}

      {toast.show && <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />}
    </div>
  );
};

export default ValidationModule;